import type { HalftoneData, HalftoneHeader, DotGroups } from "./types.js";
import {
  HALFTONE_BYTE_LAYOUT,
  PALETTE_SIZE,
  halftoneBinByteLength,
  halftoneFloatBytes,
} from "./types.js";
import { decodeGzip } from "./compression.js";

export { decodeGzip };

/** Unpacks the binary for a header that's already been checked. Rejects a binary of the wrong
    length, a coordinate or radius that isn't finite, and a color index outside the palette, so
    bad data fails here and not somewhere in a shader. */
export function unpackHalftone(header: HalftoneHeader, bin: ArrayBufferLike): HalftoneData {
  const { n } = header;
  const expected = halftoneBinByteLength(n);
  if (bin.byteLength !== expected)
    throw new Error(`halftone binary: ${bin.byteLength} bytes, expected ${expected} for ${n} dots`);
  const floatCount = HALFTONE_BYTE_LAYOUT.floats.length;
  const floatBytes = halftoneFloatBytes(n);
  const f32 = new Float32Array(bin, 0, n * floatCount);
  if (!f32.every(Number.isFinite)) throw new Error("halftone binary: a coordinate is not finite");
  const colorIndex = new Uint8Array(bin, floatBytes, n);
  if (colorIndex.some((index) => index >= header.colors.length))
    throw new Error("halftone binary: a color index is outside the palette");
  return {
    w: header.w,
    h: header.h,
    n,
    x: f32.subarray(0, n),
    y: f32.subarray(n, 2 * n),
    r: f32.subarray(2 * n, 3 * n),
    colorIndex,
    colors: header.colors,
  };
}

export function unpackGroups(json: Record<string, number[]>): DotGroups {
  const out: DotGroups = {};
  for (const [k, arr] of Object.entries(json)) out[k] = Uint32Array.from(arr);
  return out;
}

function isStringArray(x: unknown): x is string[] {
  return Array.isArray(x) && x.every((v) => typeof v === "string");
}

/** Checks the fetched header JSON. This is where data from the network enters the engine, so
    it throws on anything malformed. */
export function assertHalftoneHeader(x: unknown): asserts x is HalftoneHeader {
  if (typeof x !== "object" || x === null) {
    throw new Error("halftone header: expected an object");
  }
  const w: unknown = Reflect.get(x, "w");
  const h: unknown = Reflect.get(x, "h");
  const n: unknown = Reflect.get(x, "n");
  const colors: unknown = Reflect.get(x, "colors");
  const positive = (value: unknown): boolean =>
    typeof value === "number" && Number.isFinite(value) && value > 0;
  if (!positive(w) || !positive(h)) {
    throw new Error("halftone header: w and h must be positive numbers");
  }
  if (typeof n !== "number" || !Number.isSafeInteger(n) || n < 0) {
    throw new Error("halftone header: n must be a whole number of dots");
  }
  if (
    !isStringArray(colors) ||
    colors.length === 0 ||
    colors.length > PALETTE_SIZE ||
    !colors.every((color) => /^#[\da-f]{6}$/i.test(color))
  ) {
    throw new Error(`halftone header: colors must be 1 to ${PALETTE_SIZE} #rrggbb strings`);
  }
}

/** Checks the fetched groups JSON: every value has to be an array of dot indexes. */
export function assertDotGroupsJson(x: unknown): asserts x is Record<string, number[]> {
  if (typeof x !== "object" || x === null || Array.isArray(x)) {
    throw new Error("groups: expected an object");
  }
  for (const [k, v] of Object.entries(x)) {
    if (!Array.isArray(v) || !v.every((el) => Number.isSafeInteger(el) && el >= 0)) {
      throw new Error(`groups: value at "${k}" must be an array of dot indexes`);
    }
  }
}

/** Rejects a group that points at a dot past the halftone's `n` dots. */
export function assertGroupsFit(groups: DotGroups, n: number): void {
  for (const [name, indexes] of Object.entries(groups)) {
    if (indexes.some((index) => index >= n))
      throw new Error(`groups: "${name}" names a dot past the ${n} dots`);
  }
}

export interface LoadOptions {
  gzip?: boolean;
  /** Aborts the requests, including a body that's still streaming in. */
  signal?: AbortSignal;
}

function requireOk(response: Response, resource: string): Response {
  if (!response.ok) throw new Error(`${resource}: request failed with ${response.status}`);
  return response;
}

export async function loadHalftone(
  urls: { header: string; binary: string },
  options: LoadOptions = {},
): Promise<HalftoneData> {
  const init = { signal: options.signal ?? null };
  const [headerJson, bin]: [unknown, ArrayBuffer] = await Promise.all([
    fetch(urls.header, init).then((response) => requireOk(response, "halftone header").json()),
    fetch(urls.binary, init)
      .then((response) => requireOk(response, "halftone binary").arrayBuffer())
      .then((buffer) => (options.gzip ? decodeGzip(buffer) : buffer)),
  ]);
  assertHalftoneHeader(headerJson);
  return unpackHalftone(headerJson, bin);
}

export async function loadGroups(url: string, options: LoadOptions = {}): Promise<DotGroups> {
  const json: unknown = await fetch(url, { signal: options.signal ?? null }).then((response) =>
    requireOk(response, "groups").json(),
  );
  assertDotGroupsJson(json);
  return unpackGroups(json);
}
